import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PAGE_SIZE } from '@/lib/constants';

interface PaginationProps {
  page: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  pageSize?: number;
  itemLabel?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  page,
  totalCount,
  onPageChange,
  pageSize = PAGE_SIZE,
  itemLabel = 'results',
}) => {
  if (totalCount === 0) return null;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const start = page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, totalCount);

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-brand-border bg-white rounded-b-lg">
      <p className="text-xs text-brand-muted">
        Showing <span className="font-semibold text-brand-black">{start}–{end}</span> of{' '}
        <span className="font-semibold text-brand-black">{totalCount}</span> {itemLabel}
      </p>
      {totalPages > 1 && (
        <div className="flex items-center space-x-1">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page === 0}
            className="p-1.5 rounded-md text-brand-black hover:bg-brand-bg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs font-medium text-brand-black px-2">
            {page + 1} / {totalPages}
          </span>
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages - 1}
            className="p-1.5 rounded-md text-brand-black hover:bg-brand-bg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};
